import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "./ui/button";

const LOGO =
  "https://customer-assets.emergentagent.com/job_drone-survey-pro-4/artifacts/tzmknqwz_Snesar-Logo-1000%20%281%29.png";

const links = [
  { label: "Services", href: "#services" },
  { label: "About", href: "#about" },
  { label: "Project", href: "#project" },
  { label: "Portfolio", href: "#portfolio" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setOpen(false);
    const id = href.replace("#", "");
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-gray-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <a
          href="#home"
          data-testid="nav-logo"
          onClick={(e) => {
            e.preventDefault();
            scrollTo("#home");
          }}
          className="flex items-center gap-3"
        >
          <img src={LOGO} alt="Snesar Aerial Intelligence Pvt Ltd" className="h-9 sm:h-10 w-auto" />
          <span className="hidden lg:flex flex-col leading-tight border-l border-gray-300 pl-3">
            <span className="font-heading text-sm font-black text-[#10193f] tracking-tight">
              Snesar Aerial Intelligence
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8b8f99]">
              Pvt Ltd
            </span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => scrollTo(l.href)}
              className="text-sm font-semibold text-[#10193f]/80 hover:text-[#10193f] transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="tel:+917715972997"
            data-testid="nav-phone"
            className="flex items-center gap-2 text-sm font-semibold text-[#10193f]"
          >
            <Phone className="h-4 w-4" /> 7715972997
          </a>
          <Button
            data-testid="nav-quote-btn"
            onClick={() => scrollTo("#contact")}
            className="rounded-sm bg-[#10193f] hover:bg-[#1a2350] text-white font-semibold px-5"
          >
            Get a Quote
          </Button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-[#10193f]"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-white border-b border-gray-200 px-5 pb-6 pt-2 space-y-2"
        >
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
              onClick={() => scrollTo(l.href)}
              className="block w-full text-left py-2 text-base font-semibold text-[#10193f]"
            >
              {l.label}
            </button>
          ))}
          <Button
            data-testid="mobile-nav-quote-btn"
            onClick={() => scrollTo("#contact")}
            className="w-full rounded-sm bg-[#10193f] hover:bg-[#1a2350] text-white font-semibold mt-2"
          >
            Get a Quote
          </Button>
        </div>
      )}
    </header>
  );
};
